<template>
  <v-app class="navigation-app" :style="{ '--header-h': headerHeight + 'px' }">
    <div class="page-background"></div>

    <v-app-bar app flat :height="headerHeight" class="navigation-header">
      <v-btn icon class="back-button" aria-label="Kembali" @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <div class="header-copy">
        <div class="header-eyebrow">BONDOWOSO BERKAH</div>
        <v-toolbar-title class="header-title">
          {{ selectedName || "Navigasi Peta" }}
        </v-toolbar-title>
      </div>

      <v-spacer></v-spacer>

      <div class="live-status">
        <span class="status-dot"></span>
        <span class="status-label">PETA AKTIF</span>
      </div>
    </v-app-bar>

    <v-main class="navigation-main">
      <div class="map-layout">
        <section class="map-stage">
          <div id="map"></div>

          <div class="map-top-chip">
            <v-icon small>mdi-map-marker-path</v-icon>
            <span>Petunjuk arah</span>
          </div>

          <div class="map-action-stack">
            <v-btn
              fab
              small
              class="map-action-button"
              aria-label="Aktifkan atau matikan suara"
              @click="toggleVoice"
            >
              <v-icon>{{ voiceOn ? "mdi-volume-high" : "mdi-volume-off" }}</v-icon>
            </v-btn>
            <v-btn
              fab
              small
              class="map-action-button"
              aria-label="Kembali ke posisi saya"
              @click="recenter"
            >
              <v-icon>mdi-crosshairs-gps</v-icon>
            </v-btn>
          </div>

          <div class="map-legend">
            <span class="legend-item"
              ><i class="legend-dot user-dot"></i> Posisi Anda</span
            >
            <span class="legend-item"
              ><i class="legend-dot destination-dot"></i> Tujuan</span
            >
          </div>
        </section>

        <aside class="destination-panel">
          <div class="panel-handle"></div>

          <div class="destination-heading">
            <div class="destination-icon">
              <v-icon>mdi-map-marker</v-icon>
            </div>
            <div class="destination-copy">
              <div class="section-eyebrow">TUJUAN PERJALANAN</div>
              <h2>{{ selectedName || "Lokasi tujuan" }}</h2>
              <p>{{ selectedAddr || "Alamat tujuan akan ditampilkan di sini." }}</p>
            </div>
          </div>

          <div class="route-info">
            <div class="route-info-icon">
              <v-icon small>mdi-navigation-variant</v-icon>
            </div>
            <div>
              <div class="route-info-title">Navigasi langsung</div>
              <div class="route-info-caption">
                Ikuti jalur pada peta dan petunjuk suara
              </div>
            </div>
          </div>

          <div class="panel-actions">
            <v-btn
              depressed
              class="voice-button"
              :class="{ 'voice-button-active': voiceOn }"
              @click="toggleVoice"
            >
              <v-icon left>{{ voiceOn ? "mdi-volume-high" : "mdi-volume-off" }}</v-icon>
              {{ voiceOn ? "Suara aktif" : "Aktifkan suara" }}
            </v-btn>
            <v-btn depressed class="recenter-button" @click="recenter">
              <v-icon left>mdi-crosshairs-gps</v-icon>
              Posisi saya
            </v-btn>
          </div>

          <div class="panel-footnote">
            <v-icon small>mdi-information-outline</v-icon>
            <span
              >Pastikan GPS aktif dan izinkan akses lokasi agar navigasi berjalan
              akurat.</span
            >
          </div>
        </aside>
      </div>
    </v-main>
  </v-app>
</template>
<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";

export default {
  data: () => ({
    map: null,
    routingControl: null,
    selectedName: "",
    selectedAddr: "",

    watchId: null,
    userMarker: null,
    targetCoords: null,
    following: true,
    voiceOn: false,
    steps: [],
    stepPointer: 1,
    announced: {},
    routeId: 0,
    lastRoutePos: null,
    lastRouteTime: 0,
    arrived: false,
  }),

  computed: {
    // Tinggi header disinkronkan dengan Vuetify supaya padding v-main benar
    headerHeight() {
      return this.$vuetify.breakpoint.width < 700 ? 64 : 78;
    },
  },

  watch: {
    // Saat tinggi header berubah (rotasi/resize), ukur ulang peta
    headerHeight() {
      this.$nextTick(() => this.refreshMapSize());
    },
  },

  async mounted() {
    // Tunggu layout selesai dulu supaya Leaflet membaca ukuran container yang benar
    await this.$nextTick();
    this.initMap();

    const { name, latlong, addr } = this.$route.query;
    if (latlong) {
      this.selectedName = name;
      this.selectedAddr = addr;
      const coords = latlong.split(",").map(Number);

      this.map.setView(coords, 16);
      this.addSingleMarker(coords);
      this.showRoute(coords);
    }

    this.refreshMapSize();
  },

  beforeDestroy() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._onWindowResize) {
      window.removeEventListener("resize", this._onWindowResize);
      window.removeEventListener("orientationchange", this._onWindowResize);
      this._onWindowResize = null;
    }
    clearTimeout(this._resizeTimer);

    if (this.map) this.map.remove();
  },

  methods: {
    initMap() {
      this.map = L.map("map", { zoomControl: false }).setView([-7.9135, 113.8217], 13);
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 20,
      }).addTo(this.map);
      L.control.zoom({ position: "bottomright" }).addTo(this.map);

      // Pantau perubahan ukuran container (rotasi, address bar mobile, dll.)
      const mapEl = document.getElementById("map");
      const onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          if (this.map) this.map.invalidateSize();
        }, 120);
      };

      if (typeof ResizeObserver !== "undefined" && mapEl) {
        this._resizeObserver = new ResizeObserver(onResize);
        this._resizeObserver.observe(mapEl);
      }

      this._onWindowResize = onResize;
      window.addEventListener("resize", this._onWindowResize);
      window.addEventListener("orientationchange", this._onWindowResize);
    },

    refreshMapSize() {
      if (!this.map) return;
      this.map.invalidateSize();
      [100, 350, 800].forEach((delay) => {
        setTimeout(() => {
          if (this.map) this.map.invalidateSize();
        }, delay);
      });
    },

    addSingleMarker(coords) {
      this.createCustomMarker(coords, "#018c74", "LOKASI TUJUAN");
    },

    createCustomMarker(coords, color, label) {
      const customIcon = L.divIcon({
        className: "cyber-marker",
        html: `
          <div class="marker-pulse" style="background: ${color}44; border-color: ${color}"></div>
          <div class="marker-core" style="background: ${color}; box-shadow: 0 0 10px ${color}"></div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker(coords, { icon: customIcon }).addTo(this.map);

      if (label) {
        marker.bindTooltip(label, {
          permanent: true,
          direction: "top",
          offset: [0, -14],
          className: "leaflet-tooltip-cyber",
        });
      }

      return marker;
    },

    showRoute(targetCoords) {
      if (!navigator.geolocation) return alert("Geolocation tidak didukung");
      this.targetCoords = targetCoords;

      this.map.on("dragstart", () => (this.following = false));

      this.watchId = navigator.geolocation.watchPosition(
        (pos) => this.onPosition(pos),
        (err) => console.warn("GPS error:", err.message),
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
      );
    },

    onPosition(pos) {
      const { latitude, longitude, accuracy } = pos.coords;
      if (accuracy > 100) return;

      const userCoords = [latitude, longitude];
      const userLatLng = L.latLng(userCoords);

      if (!this.userMarker) {
        this.userMarker = this.createCustomMarker(userCoords, "#762dcf", "POSISI ANDA");
        this.buildRoute(userLatLng);
        this.map.fitBounds(L.latLngBounds([userCoords, this.targetCoords]), {
          padding: [80, 80],
          maxZoom: 15,
          animate: false,
        });
        return;
      }

      this.userMarker.setLatLng(userLatLng);
      if (this.following) this.map.panTo(userLatLng, { animate: true });

      this.updateNavigation(userLatLng);
    },

    buildRoute(userLatLng) {
      this.lastRoutePos = userLatLng;
      this.lastRouteTime = Date.now();

      if (this.routingControl) {
        this.routingControl.spliceWaypoints(0, 1, userLatLng);
        return;
      }

      this.routingControl = L.Routing.control({
        waypoints: [userLatLng, L.latLng(this.targetCoords)],
        router: L.Routing.osrmv1({
          serviceUrl: "https://router.project-osrm.org/route/v1",
          language: "id",
        }),
        lineOptions: { styles: [{ color: "#cf2d2d", weight: 6, opacity: 0.8 }] },
        createMarker: () => null,
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: false,
      }).addTo(this.map);

      this.routingControl.on("routesfound", (e) => {
        const route = e.routes[0];

        e.routes.forEach((r) => {
          r.instructions.forEach((ins) => {
            ins.text = this.terjemahkanInstruksi(ins.text);
          });
        });

        this.routeId++;
        this.announced = {};
        this.stepPointer = 1;
        this.steps = route.instructions.map((ins) => ({
          text: ins.text,
          latlng: L.latLng(route.coordinates[ins.index]),
        }));
      });
    },

    updateNavigation(userLatLng) {
      if (this.arrived) return;

      if (userLatLng.distanceTo(L.latLng(this.targetCoords)) < 25) {
        this.arrived = true;
        this.speak("Anda telah tiba di tujuan");
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
        return;
      }

      const moved = userLatLng.distanceTo(this.lastRoutePos);
      if (moved > 40 && Date.now() - this.lastRouteTime > 8000) {
        this.buildRoute(userLatLng);
      }

      const step = this.steps[this.stepPointer];
      if (!step) return;
      const d = userLatLng.distanceTo(step.latlng);
      const key = `${this.routeId}-${this.stepPointer}`;

      if (d < 30) {
        this.speak(step.text);
        this.stepPointer++;
      } else if (d < 150 && !this.announced[key]) {
        this.announced[key] = true;
        this.speak(`Dalam ${Math.round(d / 10) * 10} meter, ${step.text}`);
      }
    },

    speak(text) {
      if (!this.voiceOn || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "id-ID";
      u.rate = 1;
      window.speechSynthesis.speak(u);
    },

    toggleVoice() {
      this.voiceOn = !this.voiceOn;
      if (this.voiceOn) this.speak("Suara navigasi aktif");
      else if (window.speechSynthesis) window.speechSynthesis.cancel();
    },

    recenter() {
      this.following = true;
      if (this.userMarker) this.map.panTo(this.userMarker.getLatLng());
    },

    terjemahkanInstruksi(text) {
      const kamus = [
        [
          /Head (north|northeast|east|southeast|south|southwest|west|northwest) on/i,
          "Menuju arah $1 di",
        ],
        [/Make a sharp left onto/i, "Belok kiri tajam ke"],
        [/Make a sharp right onto/i, "Belok kanan tajam ke"],
        [/Make a slight left onto/i, "Belok sedikit ke kiri ke"],
        [/Make a slight right onto/i, "Belok sedikit ke kanan ke"],
        [/Turn left to stay on/i, "Belok kiri untuk tetap di"],
        [/Turn right to stay on/i, "Belok kanan untuk tetap di"],
        [/Turn left onto/i, "Belok kiri ke"],
        [/Turn right onto/i, "Belok kanan ke"],
        [/Turn left/i, "Belok kiri"],
        [/Turn right/i, "Belok kanan"],
        [/Continue straight/i, "Lurus terus"],
        [/Continue/i, "Lanjutkan"],
        [/Make a U-turn/i, "Putar balik"],
        [/You have arrived at your destination/i, "Anda telah tiba di tujuan"],
        [/Destination reached/i, "Tujuan tercapai"],
        [
          /Enter the roundabout and take the (\d+)\w* exit/i,
          "Masuk bundaran, ambil jalan keluar ke-$1",
        ],
        [/Enter the roundabout/i, "Masuk bundaran"],
        [/\bnorthwest\b/i, "barat laut"],
        [/\bnortheast\b/i, "timur laut"],
        [/\bsouthwest\b/i, "barat daya"],
        [/\bsoutheast\b/i, "tenggara"],
        [/\bnorth\b/i, "utara"],
        [/\bsouth\b/i, "selatan"],
        [/\beast\b/i, "timur"],
        [/\bwest\b/i, "barat"],
      ];
      return kamus.reduce((hasil, [pola, ganti]) => hasil.replace(pola, ganti), text);
    },
  },
};
</script>

<style scoped>
.navigation-app {
  --brand: #018c74;
  --brand-dark: #016b5b;
  --brand-soft: #e8f5f1;
  --ink: #18332e;
  --muted: #71817d;
  --line: #e5eeeb;
  --header-h: 78px;
  min-height: 100vh;
  background: #f4f8f7;
  color: var(--ink);
}
.page-background {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: radial-gradient(circle at 8% 5%, rgba(1, 140, 116, 0.1), transparent 27%),
    radial-gradient(circle at 95% 95%, rgba(207, 162, 45, 0.1), transparent 25%), #f4f8f7;
}
.navigation-header {
  z-index: 1000;
  height: var(--header-h) !important;
  padding: 0 28px;
  background: rgba(255, 255, 255, 0.94) !important;
  border-bottom: 1px solid rgba(1, 140, 116, 0.1);
  box-shadow: 0 5px 24px rgba(23, 65, 56, 0.06) !important;
  backdrop-filter: blur(18px);
}
.navigation-header ::v-deep .v-toolbar__content {
  height: var(--header-h) !important;
}
.back-button {
  background: #edf7f4;
  color: var(--brand) !important;
  margin-right: 14px;
}
.header-copy {
  min-width: 0;
}
.header-eyebrow,
.section-eyebrow {
  color: var(--brand);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.45px;
  line-height: 1.5;
}
.header-title {
  max-width: min(55vw, 520px);
  overflow: hidden;
  color: var(--ink);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.35px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid #dcefe9;
  border-radius: 999px;
  background: #f2faf7;
  color: var(--brand-dark);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  white-space: nowrap;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #21a67a;
  box-shadow: 0 0 0 4px rgba(33, 166, 122, 0.12);
}
.navigation-main {
  position: relative;
  z-index: 1;
}
.navigation-main ::v-deep .v-main__wrap {
  overflow: hidden;
}
.map-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 350px;
  gap: 18px;
  width: 100%;
  height: calc(100vh - var(--header-h));
  min-height: 480px;
  padding: 18px;
  box-sizing: border-box;
}
.map-stage {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(1, 140, 116, 0.12);
  border-radius: 24px;
  background: #e9f0ed;
  box-shadow: 0 12px 36px rgba(24, 70, 59, 0.09);
}
#map {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: #e9f0ed;
  z-index: 1;
}
.map-top-chip {
  position: absolute;
  z-index: 500;
  top: 18px;
  left: 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 15px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 5px 18px rgba(18, 54, 45, 0.1);
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
}
.map-top-chip .v-icon {
  color: var(--brand);
}
.map-action-stack {
  position: absolute;
  z-index: 500;
  top: 18px;
  right: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.map-action-button {
  color: var(--brand) !important;
  background: rgba(255, 255, 255, 0.97) !important;
  box-shadow: 0 5px 18px rgba(18, 54, 45, 0.14) !important;
}
.map-legend {
  position: absolute;
  z-index: 500;
  left: 18px;
  bottom: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 11px 14px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 5px 18px rgba(18, 54, 45, 0.1);
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #52635e;
  font-size: 11px;
  font-weight: 700;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.user-dot {
  background: #762dcf;
}
.destination-dot {
  background: var(--brand);
}
.destination-panel {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 25px 22px 20px;
  border: 1px solid rgba(1, 140, 116, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 36px rgba(24, 70, 59, 0.07);
}
.panel-handle {
  display: none;
}
.destination-heading {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--line);
}
.destination-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 15px;
  background: var(--brand-soft);
  color: var(--brand);
}
.destination-copy {
  min-width: 0;
}
.destination-copy h2 {
  margin: 5px 0 7px;
  color: var(--ink);
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.destination-copy p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.route-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 20px 0;
  padding: 15px 13px;
  border: 1px solid #e5f1ed;
  border-radius: 15px;
  background: #f7fbf9;
}
.route-info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 10px;
  background: #e5f5ef;
  color: var(--brand);
}
.route-info-title {
  color: var(--ink);
  font-size: 12px;
  font-weight: 800;
}
.route-info-caption {
  margin-top: 4px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.5;
}
.panel-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.panel-actions ::v-deep .v-btn {
  height: 46px;
  border-radius: 13px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}
.voice-button {
  background: var(--brand) !important;
  color: white !important;
  box-shadow: 0 7px 16px rgba(1, 140, 116, 0.2) !important;
}
.voice-button-active {
  background: var(--brand-dark) !important;
}
.recenter-button {
  border: 1px solid #dcece7;
  background: white !important;
  color: var(--brand-dark) !important;
}
.panel-footnote {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: auto;
  padding-top: 22px;
  color: #83918d;
  font-size: 10px;
  line-height: 1.6;
}
.panel-footnote .v-icon {
  flex: 0 0 auto;
  color: #9aaca6;
}
::v-deep .leaflet-control-zoom {
  overflow: hidden;
  border: 0 !important;
  border-radius: 12px !important;
  box-shadow: 0 5px 18px rgba(18, 54, 45, 0.14) !important;
}
::v-deep .leaflet-control-zoom a {
  width: 36px !important;
  height: 36px !important;
  line-height: 36px !important;
  border: 0 !important;
  color: var(--brand-dark) !important;
  background: white !important;
}
::v-deep .leaflet-control-attribution {
  font-size: 9px;
  background: rgba(255, 255, 255, 0.82) !important;
}
::v-deep .leaflet-tooltip-cyber {
  padding: 7px 10px !important;
  border: 1px solid #d8eee7 !important;
  border-radius: 9px !important;
  background: white !important;
  color: #24473e !important;
  box-shadow: 0 4px 15px rgba(20, 60, 50, 0.12) !important;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
::v-deep .leaflet-tooltip-cyber::before {
  display: none;
}
::v-deep .marker-core {
  position: relative;
  z-index: 2;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-radius: 50%;
}
::v-deep .marker-pulse {
  position: absolute;
  top: -8px;
  left: -8px;
  z-index: 1;
  width: 30px;
  height: 30px;
  border: 2px solid var(--brand);
  border-radius: 50%;
  animation: marker-pulse 2s infinite;
}
@keyframes marker-pulse {
  0% {
    transform: scale(0.5);
    opacity: 0.9;
  }
  100% {
    transform: scale(2.3);
    opacity: 0;
  }
}

/* Tablet */
@media (max-width: 960px) {
  .map-layout {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 12px;
    padding: 12px;
  }
  .destination-panel {
    padding: 21px 17px 17px;
  }
  .destination-copy h2 {
    font-size: 17px;
  }
}

/* HP landscape / layar pendek: tetap berdampingan tapi lebih ringkas */
@media (max-height: 560px) and (orientation: landscape) {
  .map-layout {
    min-height: 0;
    padding: 10px;
    gap: 10px;
  }
  .destination-panel {
    padding: 14px;
    border-radius: 18px;
  }
  .destination-heading {
    padding-bottom: 10px;
  }
  .route-info,
  .panel-footnote {
    display: none;
  }
  .panel-actions {
    margin-top: 12px;
  }
}

/* HP portrait */
@media (max-width: 700px) {
  .navigation-header {
    padding: 0 12px;
  }
  .navigation-header ::v-deep .v-toolbar__content {
    padding: 0 4px;
  }
  .back-button {
    margin-right: 8px;
  }
  .header-eyebrow {
    font-size: 8px;
    letter-spacing: 1px;
  }
  .header-title {
    max-width: 48vw;
    font-size: 14px;
  }
  .live-status {
    gap: 6px;
    padding: 8px 9px;
    font-size: 8px;
  }
  .map-layout {
    display: flex;
    flex-direction: column;
    gap: 0;
    height: calc(100vh - var(--header-h));
    height: calc(100dvh - var(--header-h));
    min-height: 0;
    padding: 0;
  }
  .map-stage {
    flex: 1 1 0;
    min-height: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
  .map-top-chip {
    top: 12px;
    left: 12px;
    padding: 8px 11px;
    font-size: 11px;
  }
  .map-action-stack {
    top: 12px;
    right: 12px;
    gap: 8px;
  }
  .map-legend {
    left: 12px;
    bottom: 12px;
    gap: 10px;
    padding: 8px 10px;
  }
  .legend-item {
    font-size: 10px;
  }
  .destination-panel {
    position: relative;
    z-index: 600;
    flex: 0 0 auto;
    max-height: 45%;
    padding: 10px 16px calc(14px + env(safe-area-inset-bottom, 0px));
    border: 0;
    border-top: 1px solid #e3eeea;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -8px 24px rgba(20, 64, 53, 0.1);
  }
  .panel-handle {
    display: block;
    flex: 0 0 4px;
    width: 38px;
    height: 4px;
    margin: 0 auto 12px;
    border-radius: 99px;
    background: #d9e5e1;
  }
  .destination-heading {
    gap: 11px;
    padding-bottom: 12px;
    border-bottom: 0;
  }
  .destination-icon {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
    border-radius: 12px;
  }
  .destination-copy h2 {
    margin: 2px 0 3px;
    font-size: 16px;
  }
  .destination-copy p {
    display: -webkit-box;
    overflow: hidden;
    font-size: 11px;
    line-height: 1.45;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .section-eyebrow {
    font-size: 9px;
  }
  /* Disembunyikan di HP supaya peta mendapat ruang lebih besar */
  .route-info,
  .panel-footnote {
    display: none;
  }
  .panel-actions {
    flex-direction: row;
  }
  .panel-actions ::v-deep .v-btn {
    flex: 1 1 0;
    min-width: 0;
    height: 42px;
    padding: 0 8px;
    font-size: 11px;
  }
  .panel-actions ::v-deep .v-btn .v-btn__content {
    white-space: nowrap;
  }
  ::v-deep .leaflet-routing-container,
  ::v-deep .leaflet-routing-container-hide,
  ::v-deep .leaflet-routing-alternatives-container {
    display: none !important;
  }
}

/* HP kecil */
@media (max-width: 380px) {
  .header-title {
    max-width: 42vw;
    font-size: 12px;
  }
  .live-status {
    padding: 7px;
  }
  .status-label {
    display: none;
  }
  .panel-actions ::v-deep .v-btn {
    font-size: 10px;
  }
  .destination-copy h2 {
    font-size: 14px;
  }
}
</style>
